import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import {
  RefreshCw,
  Sun,
  Moon,
  Settings,
  PieChart,
  History,
  ChevronDown,
  LogOut,
  User,
  Grid,
  Landmark,
  Eye,
  EyeOff,
  Bookmark,
  Wallet,
  CreditCard,
} from "lucide-react";
import { usePortfolioStore } from "../store/portfolioStore";
import { useThemeStore } from "../store/themeStore";
import { useAuthStore } from "../store/authStore";
import { useFxRateStore } from "../store/fxRateStore";
import { useConfigStore } from "../store/configStore";
import Button from "./ui/Button";

function headerDaysUntil(due_day: number): number {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const y = today.getFullYear(), mo = today.getMonth();
  let next = new Date(y, mo, due_day);
  if (next <= today) next = new Date(y, mo + 1, due_day);
  return Math.round((next.getTime() - today.getTime()) / 86400000);
}

const CreditDueDateChips: React.FC<{ compact?: boolean }> = ({ compact }) => {
  const { getConfigValue } = useConfigStore();
  let dueDates: Record<string, { card_name: string; due_day: number }> = {};
  try { dueDates = JSON.parse(getConfigValue("credit_due_dates", "{}")); } catch {}

  const entries = Object.entries(dueDates);
  if (entries.length === 0) return null;

  const filtered = compact ? entries.filter(([, cfg]) => headerDaysUntil(cfg.due_day) <= 7) : entries;
  if (filtered.length === 0) return null;

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {filtered.map(([last4, cfg]) => {
        const days = headerDaysUntil(cfg.due_day);
        const urgency = days === 0 ? "today" : days <= 3 ? "urgent" : days <= 7 ? "warning" : "ok";
        return (
          <span key={last4}
            title={`${cfg.card_name || "···" + last4} — ${days === 0 ? "วันนี้!" : `ครบกำหนด ${days} วัน`}`}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border cursor-default ${
              urgency === "today"
                ? "bg-red-100 border-red-300 text-red-600 dark:bg-red-900/30 dark:border-red-600 dark:text-red-400 animate-pulse"
                : urgency === "urgent"
                ? "bg-red-50 border-red-200 text-red-500 dark:bg-red-900/20 dark:border-red-700 dark:text-red-400"
                : urgency === "warning"
                ? "bg-warning-50 border-warning-200 text-warning-700 dark:bg-warning-900/20 dark:border-warning-700 dark:text-warning-400"
                : "bg-gray-50 border-gray-200 text-gray-600 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400"
            }`}
          >
            <CreditCard className="w-2.5 h-2.5" />
            <span className="font-mono">···{last4}</span>
            <span className="font-bold">{days === 0 ? "วันนี้" : `${days}d`}</span>
          </span>
        );
      })}
    </div>
  );
};

interface HeaderProps {
  onFxRateClick?: () => void;
}

interface UserMenuProps {
  displayCurrency: string;
  currencyOptions: { value: string; label: string; icon?: string }[];
  onCurrencyChange: (value: string) => void;
}

// User Menu Component
const UserMenu: React.FC<UserMenuProps> = ({
  displayCurrency,
  currencyOptions,
  onCurrencyChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout } = useAuthStore();
  const { isDark, toggleTheme, maskAmounts, toggleMaskAmounts } =
    useThemeStore();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={menuRef}>
      <Button variant="secondary" size="sm"
        onClick={() => setIsOpen(!isOpen)}
       
      >
        <User className="w-4 h-4" />
        <span className="hidden md:inline">{user?.username || "User"}</span>
        <ChevronDown
          className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </Button>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="absolute right-0 mt-2 bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 min-w-60 z-50 overflow-hidden"
        >
          <div className="py-2">
            {/* User info */}
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {user?.username}
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-400 mt-0.5">
                {user?.email || "No email"}
              </p>
            </div>

            {/* Net Worth */}
            <button
              onClick={() => {
                setIsOpen(false)
                window.dispatchEvent(new CustomEvent('show-net-worth-modal'))
              }}
              className="w-full flex items-center gap-2.5 px-5 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <Wallet className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              Net Worth
            </button>

            {/* Currency */}
            <button
              onClick={() => {
                const idx = currencyOptions.findIndex((o) => o.value === displayCurrency);
                const next = currencyOptions[(idx + 1) % currencyOptions.length];
                onCurrencyChange(next.value);
              }}
              className="w-full flex items-center justify-between px-5 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-2.5">
                <span className="text-base leading-none">💱</span>
                Currency
              </div>
              <span className="text-xs text-gray-400 dark:text-gray-400">
                {currencyOptions.find((o) => o.value === displayCurrency)?.icon}{" "}
                {displayCurrency}
              </span>
            </button>

            {/* Theme */}
            <button
              onClick={toggleTheme}
              className="w-full flex items-center justify-between px-5 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-2.5">
                {isDark ? (
                  <Moon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                ) : (
                  <Sun className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                )}
                Theme
              </div>
              <span className="text-xs text-gray-400 dark:text-gray-400">
                {isDark ? "Dark" : "Light"}
              </span>
            </button>

            {/* Hide amounts */}
            <button
              onClick={toggleMaskAmounts}
              className="w-full flex items-center justify-between px-5 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-2.5">
                {maskAmounts ? (
                  <EyeOff className="w-4 h-4 text-gray-400" />
                ) : (
                  <Eye className="w-4 h-4 text-gray-400" />
                )}
                Hide amounts
              </div>
              <div
                className={`relative w-9 h-5 rounded-full transition-colors ${maskAmounts ? "bg-gradient-to-r from-blue-500 to-purple-600" : "bg-gray-300 dark:bg-gray-600"}`}
              >
                <div
                  className={`absolute top-1 w-3 h-3 bg-white rounded-full shadow transition-transform ${maskAmounts ? "translate-x-5" : "translate-x-1"}`}
                />
              </div>
            </button>

            <div className="border-t border-gray-100 dark:border-gray-700 mx-3 my-1" />

            {/* App Version */}
            <div className="px-5 py-2 flex items-center justify-between">
              <span className="text-xs text-gray-400 dark:text-gray-400">Version</span>
              <span className="text-xs font-mono text-gray-400 dark:text-gray-400">v{__APP_VERSION__}</span>
            </div>

            <div className="border-t border-gray-100 dark:border-gray-700 mx-3 my-1" />

            {/* Sign Out */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2.5 px-5 py-3 text-sm font-medium text-loss hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
};

const Header: React.FC<HeaderProps> = ({ onFxRateClick }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get current view from URL
  const getCurrentView = () => {
    const path = location.pathname;
    if (path === "/trading") return "trading";
    if (path === "/banking") return "banking";
    if (path === "/heatmap") return "heatmap";
    if (path === "/watchlist") return "watchlist";
    if (path === "/settings") return "management";
    if (path === "/credit") return "credit";
    return "portfolio";
  };

  const currentView = getCurrentView();
  const {
    portfolio,
    displayCurrency,
    updating,
    fetchAllPrices,
    setDisplayCurrency,
    customPortfolios,
    selectedPortfolioId,
    setSelectedPortfolio,
    loadCustomPortfolios,
    currencies,
    loadCurrencies,
    exchangeRate,
  } = usePortfolioStore();

  const [showPortfolioDropdown, setShowPortfolioDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { zoneCache, loadZoneData } = useFxRateStore();
  const fxZone1 = zoneCache[1];

  const fxZoneBadgeColor = (zone: string) => {
    switch (zone) {
      case "Extreme Low":
        return "bg-green-500";
      case "Low":
        return "bg-green-400";
      case "Neutral":
        return "bg-yellow-400";
      case "High":
        return "bg-orange-400";
      case "Extreme High":
        return "bg-red-500";
      default:
        return "bg-gray-400";
    }
  };

  // Fetch FX zone once on mount — data is session-static, store guards duplicates
  useEffect(() => {
    loadCurrencies();
    loadZoneData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load custom portfolios when navigating to portfolio view
  useEffect(() => {
    if (currentView === "portfolio") {
      loadCustomPortfolios();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView]);

  // Handle clicks outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowPortfolioDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Convert database currencies to options format
  const currencyOptions = currencies.map((currency) => ({
    value: currency.value,
    label: currency.label,
    icon: currency.icon,
  }));

  return (
    <motion.header
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="safe-top sticky top-0 z-40 backdrop-blur-xl bg-white/85 dark:bg-gray-900/85 border-b border-gray-200 dark:border-gray-800 no-print"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 lg:h-20">
          {/* Logo & Title */}
          <motion.div
            className="flex items-center gap-3 cursor-pointer"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate("/")}
          >
            {/* Wordmark */}
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-2 leading-none">
                <span className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                  Finance
                </span>
                <span className="text-[10px] lg:text-[11px] font-bold bg-brand px-1.5 py-0.5 rounded-md tracking-widest">
                  OS
                </span>
              </div>
              <span className="text-[10px] text-gray-400 dark:text-gray-400 tracking-widest uppercase font-medium">
                Personal Finance
              </span>
            </div>
          </motion.div>

          {/* Controls */}
          <div className="flex items-center gap-2">
            {/* User Menu - mobile only */}
            <div className="lg:hidden">
              <UserMenu
                displayCurrency={displayCurrency}
                currencyOptions={currencyOptions}
                onCurrencyChange={(value) =>
                  setDisplayCurrency(value as "USD" | "THB")
                }
              />
            </div>

            {/* Exchange Rate Display - Clickable */}
            <button
              onClick={() => onFxRateClick?.()}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/40 transition-colors cursor-pointer"
              title="Click to view FX rate zone analysis"
            >
              <span className="hidden sm:inline text-xs font-medium text-green-700 dark:text-green-300">
                USD/THB
              </span>
              <span className="text-sm font-bold text-green-800 dark:text-green-200">
                ฿{exchangeRate.toFixed(2)}
              </span>
              {fxZone1 && (
                <span
                  className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white leading-none ${fxZoneBadgeColor(fxZone1.currentZone)}`}
                >
                  {fxZone1.currentZone}
                </span>
              )}
            </button>

            {/* Refresh */}
            <Button variant="ghost" size="icon"
              onClick={() => {
                fetchAllPrices();
                window.dispatchEvent(new CustomEvent("refresh-market-data"));
              }}
              disabled={updating || portfolio.length === 0}
             
              title="Refresh Prices"
            >
              <RefreshCw
                className={`w-4 h-4 ${updating ? "animate-spin text-primary-600" : ""}`}
              />
            </Button>
          </div>
        </div>

        {/* Navigation Bar - Always show */}
        {
          <motion.div
            className="hidden lg:flex items-center justify-between py-3 border-t border-white/10 dark:border-gray-700/50"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center space-x-6">
              {/* Navigation Pills */}
              <div className="flex items-center space-x-2">
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => {
                      if (currentView !== "portfolio") {
                        navigate("/");
                      } else {
                        setShowPortfolioDropdown(!showPortfolioDropdown);
                      }
                    }}
                    className={`flex items-center gap-2 px-5 py-3 text-sm font-medium rounded-full transition-all duration-200 ${
                      currentView === "portfolio"
                        ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white"
                        : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 dark:hover:from-blue-900/20 dark:hover:to-purple-900/20"
                    }`}
                  >
                    <PieChart className="w-4 h-4" />
                    Portfolio
                    <ChevronDown
                      className={`w-3 h-3 transition-transform ${
                        showPortfolioDropdown ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {/* Portfolio Dropdown */}
                  {showPortfolioDropdown && currentView === "portfolio" && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute top-full mt-2 left-0 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 min-w-64 z-50"
                    >
                      <div className="p-2">
                        {/* All Portfolios Option */}
                        <button
                          onClick={() => {
                            setSelectedPortfolio(null);
                            setShowPortfolioDropdown(false);
                          }}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors ${
                            !selectedPortfolioId
                              ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                              : "hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                          }`}
                        >
                          <span className="text-lg">📊</span>
                          <div>
                            <div className="font-medium">All Portfolios</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {portfolio.length} stocks
                            </div>
                          </div>
                        </button>

                        {/* Divider */}
                        {customPortfolios.length > 0 && (
                          <div className="my-2 border-t border-gray-200 dark:border-gray-600" />
                        )}

                        {/* Custom Portfolios */}
                        {customPortfolios.map((portfolioItem) => (
                          <button
                            key={portfolioItem.id}
                            onClick={() => {
                              setSelectedPortfolio(portfolioItem.id);
                              setShowPortfolioDropdown(false);
                            }}
                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors ${
                              selectedPortfolioId === portfolioItem.id
                                ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                                : "hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                            }`}
                          >
                            <span className="text-lg">
                              {portfolioItem.icon}
                            </span>
                            <div>
                              <div className="font-medium">
                                {portfolioItem.name}
                              </div>
                              {portfolioItem.description && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                  {portfolioItem.description}
                                </div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </div>
                <button
                  onClick={() => navigate("/trading")}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium rounded-full transition-all duration-200 ${
                    currentView === "trading"
                      ? "bg-gradient-to-r from-green-500 to-emerald-600 text-white"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 dark:hover:from-green-900/20 dark:hover:to-emerald-900/20"
                  }`}
                >
                  <History className="w-4 h-4" />
                  Trading
                </button>
                <button
                  onClick={() => navigate("/watchlist")}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium rounded-full transition-all duration-200 ${
                    currentView === "watchlist"
                      ? "bg-gradient-to-r from-orange-300 to-amber-600 text-white"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-orange-50 dark:hover:bg-orange-900/20"
                  }`}
                >
                  <Bookmark className="w-4 h-4" />
                  Watchlist
                </button>
                <button
                  onClick={() => navigate("/heatmap")}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium rounded-full transition-all duration-200 ${
                    currentView === "heatmap"
                      ? "bg-gradient-to-r from-pink-500 to-rose-600 text-white"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gradient-to-r hover:from-pink-50 hover:to-rose-50 dark:hover:from-pink-900/20 dark:hover:to-rose-900/20"
                  }`}
                >
                  <Grid className="w-4 h-4" />
                  Heatmap
                </button>
                <button
                  onClick={() => navigate("/banking")}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium rounded-full transition-all duration-200 ${
                    currentView === "banking"
                      ? "bg-gradient-to-r from-teal-500 to-cyan-600 text-white"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gradient-to-r hover:from-teal-50 hover:to-cyan-50 dark:hover:from-teal-900/20 dark:hover:to-cyan-900/20"
                  }`}
                >
                  <Landmark className="w-4 h-4" />
                  Banking
                </button>
                <button
                  onClick={() => navigate("/credit")}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium rounded-full transition-all duration-200 ${
                    currentView === "credit"
                      ? "bg-gradient-to-r from-pink-400 to-pink-600 text-white"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gradient-to-r hover:from-pink-50 hover:to-pink-100 dark:hover:from-pink-900/20 dark:hover:to-pink-800/20"
                  }`}
                >
                  <CreditCard className="w-4 h-4" />
                  Credit
                </button>
                <button
                  onClick={() => navigate("/settings")}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium rounded-full transition-all duration-200 ${
                    currentView === "management"
                      ? "bg-gray-500 text-white"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                  }`}
                >
                  <Settings className="w-4 h-4" />
                  Settings
                </button>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* <CreditDueDateChips /> — hidden until UI is ready */}
              {/* User Profile and Logout */}
              <UserMenu
                displayCurrency={displayCurrency}
                currencyOptions={currencyOptions}
                onCurrencyChange={(value) =>
                  setDisplayCurrency(value as "USD" | "THB")
                }
              />
            </div>
          </motion.div>
        }
      </div>
    </motion.header>
  );
};

export default Header;
